import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { whatsappAPI, messagingAPI } from '../services/api';
import {
  MessageCircle, QrCode, CheckCircle2, XCircle, Loader, Send, LogOut, RefreshCw,
  Bot, Plus, Trash2, Gauge, LayoutTemplate, X, Edit2, Clock, List, ArrowRightCircle,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import WaSwitch from '../components/messaging/WaSwitch';
import MessageLogPanel from '../components/messaging/MessageLogPanel';
import { useAuth } from '../context/AuthContext';

const STATUS_META = {
  connected:    { label: 'Connected',           color: 'var(--c-0ca30c)', bg: '#0ca30c17' },
  connecting:   { label: 'Connecting…',         color: 'var(--c-92400e)', bg: 'var(--cb-fffbeb)' },
  qr:           { label: 'Waiting for QR Scan', color: 'var(--c-1e40af)', bg: 'var(--cb-eff6ff)' },
  disconnected: { label: 'Not Connected',       color: 'var(--text-muted)', bg: 'var(--bg-alt, #f3f4f6)' },
};

const timeAgo = (d) => {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

// Polled at 2s — same as a QR pairing flow needs to feel responsive (WhatsApp's own
// QR expires and rotates every ~20-60s), but this is a status/admin page, not a live
// bandwidth graph, so no ring-buffer/history plumbing is needed here.
const STATUS_POLL_MS = 2000;

// Phone-only collapsible cards: the wa-closed class hides the body only inside .app-view (appView.css),
// so on desktop the header click does nothing visible and every card stays open.
function useFold(closed = false) {
  const [c, setC] = useState(closed);
  return {
    cardClass: `card mb-6 wa-fold${c ? ' wa-closed' : ''}`,
    headProps: { className: 'card-header wa-fold-head', onClick: () => setC(v => !v) },
  };
}

// Reused by both the Deropo Template editor and a keyword rule's plain-text image —
// the upload endpoint (Message Center's, shared rather than duplicated) returns a
// relative /uploads/... path, fine for the browser but not for Baileys or Deropo,
// which both fetch the image themselves from wherever this Node process runs — so
// absolutize immediately using the page's own origin (same host serves API + uploads)
// rather than storing a relative path that would silently fail to send later.
async function uploadImageAbsolute(file) {
  const res = await messagingAPI.uploadTemplateImage(file);
  const relative = res.data?.data?.url || '';
  if (!relative) return '';
  return /^https?:\/\//i.test(relative) ? relative : `${window.location.origin}${relative.startsWith('/') ? '' : '/'}${relative}`;
}

export default function WhatsAppPage() {
  useDarkReady();
  const [tab, setTab] = useState('overview'); // overview | automation | deropoTemplates | log
  const { isAdmin, hasModuleRight } = useAuth();
  const canLog = isAdmin || hasModuleRight('messaging', 'view');

  return (
    <div>
      <WaSwitch active="unofficial" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <MessageCircle size={22} color="var(--primary)" />
        <h2 style={{ margin: 0 }}>WhatsApp API</h2>
      </div>
      <p className="wa-hint" style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginTop: 2, marginBottom: 16 }}>
        Self-hosted WhatsApp gateway (Baileys).
        Nothing here sends automatically (complaint alerts, OTPs, cron reminders) unless
        Automation is explicitly turned on below; Message Center's broadcast composer can
        opt into this gateway per-send.
      </p>

      <div className="wa-tabs" style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {[
          { key: 'overview', label: 'Overview', icon: MessageCircle },
          { key: 'automation', label: 'Automation', icon: Bot },
          { key: 'deropoTemplates', label: 'Templates', icon: LayoutTemplate },
          ...(canLog ? [{ key: 'log', label: 'Message Log', icon: List }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 4px', marginBottom: -1,
              background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.key ? 'var(--primary)' : 'transparent'}`,
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 600, fontSize: '.85rem', cursor: 'pointer',
            }}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? <OverviewTab /> : tab === 'automation' ? <AutomationTab /> : tab === 'log' ? <MessageLogPanel integration="baileys" /> : <DeropoTemplatesTab />}
    </div>
  );
}

function OverviewTab() {
  const [status, setStatus] = useState(null); // { status, phoneNumber, qr }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);
  const [pendingQueue, setPendingQueue] = useState([]);
  const testFold = useFold(true);
  const recentFold = useFold(false);

  const loadRecent = useCallback(() => {
    whatsappAPI.getRecent(20).then(({ data }) => setRecent(data.data || [])).catch(() => {});
  }, []);

  const loadQueueStatus = useCallback(() => {
    whatsappAPI.getQueueStatus().then(({ data }) => setQueueStatus(data.data || null)).catch(() => {});
  }, []);

  const loadPendingQueue = useCallback(() => {
    whatsappAPI.getPendingQueue().then(({ data }) => setPendingQueue(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      whatsappAPI.getStatus().then(({ data }) => {
        if (mounted) setStatus(data.data);
      }).catch((err) => {
        if (mounted) setStatus({ status: 'unreachable', phoneNumber: null, qr: null, error: err.response?.data?.message });
      }).finally(() => mounted && setLoading(false));
      loadPendingQueue();
    };
    load();
    loadRecent();
    loadQueueStatus();
    const t = setInterval(load, STATUS_POLL_MS);
    return () => { mounted = false; clearInterval(t); };
  }, [loadRecent, loadQueueStatus, loadPendingQueue]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      await whatsappAPI.connect();
      toast.success('Pairing started — scan the QR code with WhatsApp.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start connection');
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('Disconnect this WhatsApp number? You will need to scan a new QR code to reconnect.')) return;
    setBusy(true);
    try {
      await whatsappAPI.logout();
      toast.success('Disconnected');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!to.trim() || !message.trim()) return;
    setSending(true);
    try {
      const { data } = await whatsappAPI.sendTest(to.trim(), message.trim());
      toast.success(data.queued ? data.message : 'Message sent');
      setMessage('');
      loadRecent();
      loadQueueStatus();
      loadPendingQueue();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
      loadRecent();
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}><Loader size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>;
  }

  const unreachable = status?.status === 'unreachable';
  const meta = STATUS_META[status?.status] || STATUS_META.disconnected;
  const connected = status?.status === 'connected';

  return (
    <div>
      {unreachable ? (
        <div className="card mb-6" style={{ textAlign: 'center', padding: '32px 0', color: 'var(--c-991b1b)' }}>
          <XCircle size={24} style={{ opacity: .6, marginBottom: 6 }} />
          <div style={{ fontWeight: 600 }}>WhatsApp gateway is not reachable</div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Is the <code>angel-isp-whatsapp</code> service running?
          </div>
        </div>
      ) : (
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>Connection</h3>
            <span style={{
              display: 'flex', alignItems: 'center', gap: 5, fontSize: '.72rem', fontWeight: 700,
              letterSpacing: '.03em', color: meta.color, background: meta.bg, borderRadius: 999, padding: '5px 10px',
            }}>
              {connected ? <CheckCircle2 size={12} /> : status?.status === 'connecting' ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <XCircle size={12} />}
              {meta.label}
            </span>
          </div>
          <div className="card-body" style={{ textAlign: 'center', padding: '24px 16px' }}>
            {connected && (
              <>
                <CheckCircle2 size={32} color="var(--c-0ca30c)" style={{ marginBottom: 8 }} />
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{status.phoneNumber}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2, marginBottom: 16 }}>Linked and ready to send</div>
                <button className="btn btn-secondary btn-sm" onClick={handleLogout} disabled={busy}>
                  <LogOut size={13} style={{ marginRight: 5 }} /> Disconnect
                </button>
              </>
            )}

            {status?.status === 'qr' && status.qr && (
              <>
                <img src={status.qr} alt="Scan with WhatsApp" style={{ width: 220, height: 220, borderRadius: 8, border: '1px solid var(--border)' }} />
                <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 12 }}>
                  WhatsApp → Linked Devices → Link a Device
                </div>
                <div className="wa-qr-hint" style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  On this phone? Show the QR on another screen — a phone can't scan its own display.
                </div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  Refreshes automatically — this QR expires after about a minute.
                </div>
              </>
            )}

            {status?.status === 'connecting' && (
              <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
                <Loader size={22} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
                <div>Starting session…</div>
              </div>
            )}

            {status?.status === 'disconnected' && (
              <>
                <QrCode size={32} color="var(--text-muted)" style={{ opacity: .5, marginBottom: 8 }} />
                <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 16 }}>No WhatsApp number linked yet.</div>
                <button className="btn btn-primary btn-sm" onClick={handleConnect} disabled={busy}>
                  {busy ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : <QrCode size={13} style={{ marginRight: 5 }} />}
                  Connect
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {pendingQueue.length > 0 && (
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>Pending Queue</h3>
            <span style={{
              fontSize: '.72rem', fontWeight: 700, letterSpacing: '.03em', color: 'var(--c-92400e)',
              background: 'var(--cb-fffbeb)', borderRadius: 999, padding: '5px 10px',
            }}>
              {pendingQueue.length} waiting
            </span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
              Queued while WhatsApp was disconnected — sends automatically, oldest first, the moment it reconnects.
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {pendingQueue.map(m => (
                <div key={m._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                  <Clock size={14} color="var(--c-b45309)" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.85rem', fontWeight: 600 }}>
                      {m.to || m.groupId} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· {m.kind}{m.source ? ` · ${m.source}` : ''}</span>
                    </div>
                    <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.payload?.message || m.payload?.caption || '—'}
                    </div>
                  </div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>{timeAgo(m.createdAt)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <PacingCard queueStatus={queueStatus} onSaved={loadQueueStatus} />

      <div className={testFold.cardClass}>
        <div {...testFold.headProps}><h3 style={{ margin: 0, fontSize: '.95rem' }}>Send Test Message</h3></div>
        <form onSubmit={handleSendTest} className="card-body" style={{ display: 'grid', gap: 12, maxWidth: 420 }}>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>To (mobile number, with country code)</label>
            <input className="form-control" value={to} onChange={e => setTo(e.target.value)} placeholder="919876543210" disabled={!connected} />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Message</label>
            <textarea className="form-control" rows={3} value={message} onChange={e => setMessage(e.target.value)} disabled={!connected} />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} disabled={!connected || sending || !to.trim() || !message.trim()}>
            {sending ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : <Send size={13} style={{ marginRight: 5 }} />}
            Send
          </button>
          {!connected && <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Connect a number above before sending a test message.</div>}
        </form>
      </div>

      <ButtonTemplateTestCard connected={connected} onSent={() => { loadRecent(); loadQueueStatus(); }} />

      <div className={recentFold.cardClass}>
        <div {...recentFold.headProps} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>Recent Activity</h3>
          <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); loadRecent(); }} title="Refresh">
            <RefreshCw size={13} />
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {recent.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: '.85rem' }}>No messages sent yet.</div>
          ) : (
            <div className="wa-scroll" style={{ maxHeight: 320, overflowY: 'auto' }}>
              {recent.map(m => (
                <div key={m._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                  {m.status === 'sent' ? <CheckCircle2 size={14} color="var(--c-0ca30c)" style={{ flexShrink: 0 }} />
                    : m.status === 'pending' ? <Clock size={14} color="var(--c-b45309)" style={{ flexShrink: 0 }} />
                    : <XCircle size={14} color="var(--c-d03b3b)" style={{ flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.85rem', fontWeight: 600 }}>{m.to} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· {m.type}{m.source ? ` · ${m.source}` : ''}</span></div>
                    <div className="wa-sub" style={{ fontSize: '.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.status === 'failed' ? (m.error || 'Failed') : m.status === 'pending' ? 'Queued — waiting for reconnect' : m.body}
                    </div>
                  </div>
                  <div title={new Date(m.createdAt).toLocaleString()} style={{ fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>{timeAgo(m.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PACING_PRESETS = [
  { key: 'ultra',   label: 'Ultra-conservative', minDelaySeconds: 5,   maxDelaySeconds: 10,  dailyLimit: 100,  hint: 'Freshly paired number, no send history' },
  { key: 'conservative', label: 'Conservative',   minDelaySeconds: 2,   maxDelaySeconds: 5,   dailyLimit: 300,  hint: 'Some history, still cautious' },
  { key: 'moderate', label: 'Moderate',           minDelaySeconds: 1,   maxDelaySeconds: 3,   dailyLimit: 500,  hint: 'Sized to established real broadcast volume' },
  { key: 'aggressive', label: 'Aggressive',       minDelaySeconds: 0.5, maxDelaySeconds: 1.5, dailyLimit: 1000, hint: 'Only after months of clean history at a lower tier' },
];

// Live-editable — changes take effect on the very next queued send (queue.js reads
// PacingSettings fresh every time), no service restart needed. Presets are quick-fills,
// not a separate mode — picking one just populates the fields, Save still applies them.
function PacingCard({ queueStatus, onSaved }) {
  const fold = useFold(true);
  const [minDelaySeconds, setMinDelaySeconds] = useState('');
  const [maxDelaySeconds, setMaxDelaySeconds] = useState('');
  const [dailyLimit, setDailyLimit] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (queueStatus && !touched) {
      setMinDelaySeconds(String(queueStatus.minDelayMs / 1000));
      setMaxDelaySeconds(String(queueStatus.maxDelayMs / 1000));
      setDailyLimit(String(queueStatus.dailyLimit));
    }
  }, [queueStatus, touched]);

  const applyPreset = (p) => {
    setTouched(true);
    setMinDelaySeconds(String(p.minDelaySeconds));
    setMaxDelaySeconds(String(p.maxDelaySeconds));
    setDailyLimit(String(p.dailyLimit));
  };

  const save = async () => {
    const min = parseFloat(minDelaySeconds), max = parseFloat(maxDelaySeconds), limit = parseInt(dailyLimit, 10);
    if (!(min >= 0) || !(max >= 0) || !(limit >= 1)) return toast.error('Enter valid delay seconds and daily limit');
    if (min > max) return toast.error('Min delay cannot be greater than max delay');
    setSaving(true);
    try {
      await whatsappAPI.savePacing({ minDelaySeconds: min, maxDelaySeconds: max, dailyLimit: limit });
      toast.success('Pacing updated — takes effect on the next send, no restart needed.');
      setTouched(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save pacing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={fold.cardClass}>
      <div {...fold.headProps} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Gauge size={15} />
        <h3 style={{ margin: 0, fontSize: '.95rem' }}>Send Pacing (Anti-Ban)</h3>
      </div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
        {queueStatus && (
          <div style={{ fontSize: '.83rem' }}>
            Today: <b>{queueStatus.sentToday}</b> / {queueStatus.dailyLimit} sends (all types combined — test, broadcast, chatbot replies)
          </div>
        )}

        <div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Presets (quick-fill, then Save)</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PACING_PRESETS.map(p => (
              <button key={p.key} type="button" className="btn btn-secondary btn-sm" onClick={() => applyPreset(p)} title={p.hint}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="wa-stack" style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Min Delay (seconds)</label>
            <input type="number" min={0} step={0.5} className="form-control" value={minDelaySeconds}
              onChange={e => { setTouched(true); setMinDelaySeconds(e.target.value); }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Max Delay (seconds)</label>
            <input type="number" min={0} step={0.5} className="form-control" value={maxDelaySeconds}
              onChange={e => { setTouched(true); setMaxDelaySeconds(e.target.value); }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Daily Limit</label>
            <input type="number" min={1} className="form-control" value={dailyLimit}
              onChange={e => { setTouched(true); setDailyLimit(e.target.value); }} />
          </div>
        </div>

        <button className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} onClick={save} disabled={saving}>
          {saving ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : null}
          Save Pacing
        </button>
        <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>
          A random delay between Min and Max is applied before every outbound message. Takes effect immediately — no restart needed.
        </div>
      </div>
    </div>
  );
}

const BUTTON_TYPES = [
  { value: 'url', label: 'Open URL' },
  { value: 'call', label: 'Call Number' },
  { value: 'quickReply', label: 'Quick Reply' },
];

// Buttons message format is WhatsApp's deprecated "template message" wire format —
// unverified until tested against a real phone (see whatsapp/session.js's
// rawSendButtons). This card exists specifically so that verification can happen from
// the admin UI before any MessageTemplate/broadcast relies on it.
function ButtonTemplateTestCard({ connected, onSent }) {
  const fold = useFold(true);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [footer, setFooter] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [buttons, setButtons] = useState([{ type: 'url', label: '', value: '' }]);
  const [sending, setSending] = useState(false);

  const addButton = () => buttons.length < 3 && setButtons([...buttons, { type: 'url', label: '', value: '' }]);
  const updateButton = (i, patch) => setButtons(buttons.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const removeButton = (i) => setButtons(buttons.filter((_, idx) => idx !== i));

  const send = async (e) => {
    e.preventDefault();
    const validButtons = buttons.filter(b => b.label.trim() && b.value.trim());
    if (!to.trim() || !message.trim() || validButtons.length === 0) {
      toast.error('To, message, and at least one complete button are required');
      return;
    }
    setSending(true);
    try {
      await whatsappAPI.sendButtonsTest({ to: to.trim(), message: message.trim(), footer: footer.trim() || undefined, imageUrl: imageUrl.trim() || undefined, buttons: validButtons });
      toast.success('Buttons message sent — check the phone to confirm it actually rendered as tappable buttons.');
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={fold.cardClass}>
      <div {...fold.headProps}><h3 style={{ margin: 0, fontSize: '.95rem' }}>Button Template Test</h3></div>
      <form onSubmit={send} className="card-body" style={{ display: 'grid', gap: 12, maxWidth: 480 }}>
        <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
          WhatsApp deprecated official support for this format — test here against a real phone before trusting it in a template or broadcast.
        </div>
        <div>
          <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>To</label>
          <input className="form-control" value={to} onChange={e => setTo(e.target.value)} placeholder="919876543210" disabled={!connected} />
        </div>
        <div>
          <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Message</label>
          <textarea className="form-control" rows={2} value={message} onChange={e => setMessage(e.target.value)} disabled={!connected} />
        </div>
        <div>
          <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Footer (optional)</label>
          <input className="form-control" value={footer} onChange={e => setFooter(e.target.value)} disabled={!connected} />
        </div>
        <div>
          <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Image URL (optional, must be publicly reachable)</label>
          <input className="form-control" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://…" disabled={!connected} />
        </div>
        <div>
          <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Buttons</label>
          {buttons.map((b, i) => (
            <div key={i} className="wa-btnrow" style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <select className="form-control" style={{ maxWidth: 120 }} value={b.type} onChange={e => updateButton(i, { type: e.target.value })} disabled={!connected}>
                {BUTTON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input className="form-control" placeholder="Label" value={b.label} onChange={e => updateButton(i, { label: e.target.value })} disabled={!connected} />
              <input className="form-control" placeholder={b.type === 'call' ? 'Phone number' : b.type === 'url' ? 'https://…' : 'Reply id'} value={b.value} onChange={e => updateButton(i, { value: e.target.value })} disabled={!connected} />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeButton(i)} disabled={!connected || buttons.length <= 1}><Trash2 size={13} /></button>
            </div>
          ))}
          {buttons.length < 3 && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={addButton} disabled={!connected}><Plus size={13} style={{ marginRight: 4 }} />Add Button</button>
          )}
        </div>
        <button type="submit" className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} disabled={!connected || sending}>
          {sending ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : <Send size={13} style={{ marginRight: 5 }} />}
          Send Test
        </button>
        {!connected && <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Connect a number above before sending a test message.</div>}
      </form>
    </div>
  );
}

const emptyRule = {
  triggers: [], matchType: 'contains', isDefault: false,
  replyType: 'text', reply: '', replyImageUrl: '', templateId: '',
  listTitle: '', listBody: '', listFooter: '', listButtonText: 'Choose an option', listSections: [],
  presenceDelaySeconds: 0, presenceType: 'typing', nextRuleId: '',
  isActive: true, sortOrder: 0,
};
const MATCH_TYPE_LABEL = { contains: 'Contains', word: 'Whole word', exact: 'Exact match' };

function AutomationTab() {
  const [settings, setSettings] = useState(null);
  const [uploadingRuleImage, setUploadingRuleImage] = useState(false);
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]); // Deropo Templates, for the reply-type picker
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRule, setEditingRule] = useState(null); // rule being added/edited, or null

  const load = useCallback(() => {
    Promise.all([whatsappAPI.getAutomationSettings(), whatsappAPI.getAutomationRules(), whatsappAPI.getDeropoTemplates()])
      .then(([s, r, t]) => { setSettings(s.data?.data || null); setRules(r.data?.data || []); setTemplates(t.data?.data || []); })
      .catch(() => toast.error('Failed to load automation settings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setSettings(s => ({ ...s, ...patch }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await whatsappAPI.saveAutomationSettings(settings);
      setSettings(data.data);
      toast.success('Automation settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addMenuItem = () => set({ menu: [...(settings.menu || []), { label: '', reply: '' }] });
  const updateMenuItem = (i, patch) => set({ menu: settings.menu.map((m, idx) => idx === i ? { ...m, ...patch } : m) });
  const removeMenuItem = (i) => set({ menu: settings.menu.filter((_, idx) => idx !== i) });

  const saveRule = async () => {
    const triggers = (editingRule.triggers || []).map(t => t.trim()).filter(Boolean);
    if (!triggers.length) return toast.error('At least one trigger keyword is required');
    if (editingRule.replyType === 'template' && !editingRule.templateId) return toast.error('Pick a template');
    if (editingRule.replyType === 'text' && !editingRule.reply.trim()) return toast.error('Reply is required');
    if (editingRule.replyType === 'list') {
      if (!editingRule.listBody.trim()) return toast.error('List body text is required');
      if (!(editingRule.listSections || []).some(s => (s.rows || []).length)) return toast.error('Add at least one option to the list');
    }
    const payload = { ...editingRule, triggers, nextRuleId: editingRule.nextRuleId || null };
    try {
      if (editingRule._id) {
        await whatsappAPI.updateAutomationRule(editingRule._id, payload);
      } else {
        await whatsappAPI.createAutomationRule(payload);
      }
      setEditingRule(null);
      load();
      toast.success('Rule saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };

  // Section/row editing for a replyType 'list' rule's option builder.
  const addListSection = () => setEditingRule(r => ({ ...r, listSections: [...(r.listSections || []), { title: '', rows: [] }] }));
  const removeListSection = (i) => setEditingRule(r => ({ ...r, listSections: r.listSections.filter((_, idx) => idx !== i) }));
  const updateListSection = (i, patch) => setEditingRule(r => ({ ...r, listSections: r.listSections.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const addListRow = (si) => updateListSection(si, { rows: [...(editingRule.listSections[si].rows || []), { title: '', description: '' }] });
  const removeListRow = (si, ri) => updateListSection(si, { rows: editingRule.listSections[si].rows.filter((_, idx) => idx !== ri) });
  const updateListRow = (si, ri, patch) => updateListSection(si, { rows: editingRule.listSections[si].rows.map((row, idx) => idx === ri ? { ...row, ...patch } : row) });

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this keyword rule?')) return;
    try {
      await whatsappAPI.deleteAutomationRule(id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const pickRuleImage = async (file) => {
    if (!file) return;
    setUploadingRuleImage(true);
    try {
      const url = await uploadImageAbsolute(file);
      setEditingRule(r => ({ ...r, replyImageUrl: url }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Image upload failed');
    } finally {
      setUploadingRuleImage(false);
    }
  };

  const toggleRuleActive = async (rule) => {
    try {
      await whatsappAPI.updateAutomationRule(rule._id, { isActive: !rule.isActive });
      load();
    } catch (err) {
      toast.error('Failed to update rule');
    }
  };

  if (loading || !settings) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}><Loader size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>;
  }

  return (
    <div>
      <div className="card mb-6">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bot size={15} />
          <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>Auto-Reply Chatbot</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!settings.enabled} onChange={e => set({ enabled: e.target.checked })} />
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </label>
        </div>
        <div className="card-body" style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
          Off by default. When on, any incoming message on the connected number is checked
          against the keyword rules below, then the numbered menu, then business-hours —
          in that order — before replying. Every conversation gets at most one auto-reply
          per cooldown window, whatever matched.
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header"><h3 style={{ margin: 0, fontSize: '.95rem' }}>Business Hours</h3></div>
        <div className="card-body" style={{ display: 'grid', gap: 12, maxWidth: 480 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem' }}>
            <input type="checkbox" checked={!!settings.businessHoursEnabled} onChange={e => set({ businessHoursEnabled: e.target.checked })} />
            Send an away-message outside business hours
          </label>
          {settings.businessHoursEnabled && (
            <>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Start</label>
                  <input type="time" className="form-control" value={settings.businessHoursStart} onChange={e => set({ businessHoursStart: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>End</label>
                  <input type="time" className="form-control" value={settings.businessHoursEnd} onChange={e => set({ businessHoursEnd: e.target.value })} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Away Message</label>
                <textarea className="form-control" rows={2} value={settings.awayMessage} onChange={e => set({ awayMessage: e.target.value })} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header"><h3 style={{ margin: 0, fontSize: '.95rem' }}>Numbered Menu</h3></div>
        <div className="card-body" style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
            Shown when a message matches no keyword rule and is within business hours (or
            business hours is off). Replying with a number sends that entry's reply.
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Greeting</label>
            <input className="form-control" value={settings.menuGreeting} onChange={e => set({ menuGreeting: e.target.value })} />
          </div>
          {(settings.menu || []).map((m, i) => (
            <div key={i} className="wa-btnrow" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', width: 18 }}>{i + 1}.</span>
              <input className="form-control" placeholder="Label (e.g. Check Balance)" value={m.label} onChange={e => updateMenuItem(i, { label: e.target.value })} />
              <input className="form-control" placeholder="Reply text" value={m.reply} onChange={e => updateMenuItem(i, { reply: e.target.value })} />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeMenuItem(i)}><Trash2 size={13} /></button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" style={{ justifySelf: 'start' }} onClick={addMenuItem}>
            <Plus size={13} style={{ marginRight: 4 }} />Add Menu Option
          </button>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Fallback Reply (used only if the menu above is empty and nothing else matched)
            </label>
            <input className="form-control" value={settings.fallbackReply} onChange={e => set({ fallbackReply: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Cooldown per sender (minutes) — avoids replying more than once in this window
            </label>
            <input type="number" min={1} className="form-control" style={{ maxWidth: 120 }} value={settings.cooldownMinutes} onChange={e => set({ cooldownMinutes: parseInt(e.target.value, 10) || 1 })} />
          </div>
          <button className="btn btn-primary btn-sm" style={{ justifySelf: 'start' }} onClick={saveSettings} disabled={saving}>
            {saving ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : null}
            Save Automation Settings
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>Keyword Rules</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setEditingRule({ ...emptyRule, sortOrder: rules.length })}>
            <Plus size={13} style={{ marginRight: 4 }} />Add Rule
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {rules.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: '.85rem' }}>No keyword rules yet — unmatched messages fall through to the menu above.</div>
          ) : (
            rules.map(r => (
              <div key={r._id} className="wa-rule" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="checkbox" checked={r.isActive} onChange={() => toggleRuleActive(r)} title={r.isActive ? 'Active' : 'Inactive'} />
                </label>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.85rem', fontWeight: 600 }}>
                    {(r.triggers || []).map(t => `"${t}"`).join(', ')} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({MATCH_TYPE_LABEL[r.matchType] || r.matchType})</span>
                    {r.isDefault && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · default</span>}
                    {r.nextRuleId && <ArrowRightCircle size={12} style={{ verticalAlign: -1, marginLeft: 5, color: 'var(--text-muted)' }} />}
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.replyType === 'template'
                      ? <span><LayoutTemplate size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{r.templateId?.name?.replace(/\*/g, '') || '(deleted template)'}</span>
                      : r.replyType === 'list'
                        ? <span><List size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{r.listBody}</span>
                        : r.reply}
                  </div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingRule({ ...emptyRule, ...r, templateId: r.templateId?._id || r.templateId || '', nextRuleId: r.nextRuleId || '' })}>Edit</button>
                <button className="btn btn-secondary btn-sm" onClick={() => deleteRule(r._id)}><Trash2 size={13} /></button>
              </div>
            ))
          )}
        </div>
      </div>

      {editingRule && (
        <div className="modal-overlay" onClick={() => setEditingRule(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3>{editingRule._id ? 'Edit Rule' : 'New Rule'}</h3>
              <button className="modal-close" onClick={() => setEditingRule(null)}><XCircle size={16} /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 12, maxHeight: '70vh', overflowY: 'auto' }}>
              <div>
                <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Trigger Keyword(s)</label>
                <input className="form-control" value={(editingRule.triggers || []).join(',')}
                  onChange={e => setEditingRule({ ...editingRule, triggers: e.target.value.split(',') })} placeholder="e.g. balance, due amount" />
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>Comma-separated — any one of these firing the rule.</div>
              </div>
              <div className="wa-stack" style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Match Type</label>
                  <select className="form-control" value={editingRule.matchType} onChange={e => setEditingRule({ ...editingRule, matchType: e.target.value })}>
                    {Object.entries(MATCH_TYPE_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', paddingBottom: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!editingRule.isDefault} onChange={e => setEditingRule({ ...editingRule, isDefault: e.target.checked })} />
                  Default (fires when nothing else matches)
                </label>
              </div>
              <div>
                <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Reply With</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setEditingRule({ ...editingRule, replyType: 'text' })}
                    className={`btn btn-sm ${editingRule.replyType === 'text' ? 'btn-primary' : 'btn-secondary'}`}>Plain Text</button>
                  <button type="button" onClick={() => setEditingRule({ ...editingRule, replyType: 'template' })}
                    className={`btn btn-sm ${editingRule.replyType === 'template' ? 'btn-primary' : 'btn-secondary'}`}>A Template</button>
                  <button type="button" onClick={() => setEditingRule({ ...editingRule, replyType: 'list' })}
                    className={`btn btn-sm ${editingRule.replyType === 'list' ? 'btn-primary' : 'btn-secondary'}`}><List size={13} style={{ marginRight: 4, verticalAlign: -2 }} />List</button>
                </div>
              </div>
              {editingRule.replyType === 'template' ? (
                <div>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Template (from Templates tab)</label>
                  <select className="form-control" value={editingRule.templateId || ''} onChange={e => setEditingRule({ ...editingRule, templateId: e.target.value })}>
                    <option value="">— Select a template —</option>
                    {templates.map(t => <option key={t._id} value={t._id}>{t.name.replace(/\*/g, '')} ({t.buttons?.length || 0} buttons{t.imageUrl ? ', image' : ''})</option>)}
                  </select>
                  {templates.length === 0 && <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 4 }}>No templates in the Templates library yet.</div>}
                </div>
              ) : editingRule.replyType === 'list' ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Title (optional)</label>
                    <input className="form-control" value={editingRule.listTitle} onChange={e => setEditingRule({ ...editingRule, listTitle: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Body</label>
                    <textarea className="form-control" rows={2} value={editingRule.listBody} onChange={e => setEditingRule({ ...editingRule, listBody: e.target.value })} />
                  </div>
                  <div className="wa-stack" style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Footer (optional)</label>
                      <input className="form-control" value={editingRule.listFooter} onChange={e => setEditingRule({ ...editingRule, listFooter: e.target.value })} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Button Text</label>
                      <input className="form-control" value={editingRule.listButtonText} onChange={e => setEditingRule({ ...editingRule, listButtonText: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Options</div>
                  {(editingRule.listSections || []).map((s, si) => (
                    <div key={si} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 8, display: 'grid', gap: 6 }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input className="form-control" style={{ fontSize: '.8rem' }} placeholder="Section title (optional)" value={s.title} onChange={e => updateListSection(si, { title: e.target.value })} />
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeListSection(si)}><Trash2 size={13} /></button>
                      </div>
                      {(s.rows || []).map((row, ri) => (
                        <div key={ri} className="wa-btnrow" style={{ display: 'flex', gap: 6, paddingLeft: 12 }}>
                          <input className="form-control" style={{ fontSize: '.8rem' }} placeholder="Option title" value={row.title} onChange={e => updateListRow(si, ri, { title: e.target.value })} />
                          <input className="form-control" style={{ fontSize: '.8rem' }} placeholder="Description (optional)" value={row.description} onChange={e => updateListRow(si, ri, { description: e.target.value })} />
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeListRow(si, ri)}><X size={13} /></button>
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary btn-sm" style={{ justifySelf: 'start', marginLeft: 12 }} onClick={() => addListRow(si)}>
                        <Plus size={12} style={{ marginRight: 3 }} />Add Option
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm" style={{ justifySelf: 'start' }} onClick={addListSection}>
                    <Plus size={13} style={{ marginRight: 4 }} />Add Section
                  </button>
                </div>
              ) : (
                <div>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Reply</label>
                  <textarea className="form-control" rows={3} value={editingRule.reply} onChange={e => setEditingRule({ ...editingRule, reply: e.target.value })} />
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>
                    Live tokens: <code>[wa_name]</code> <code>[me_wa_name]</code> <code>[greet]</code> <code>[now_format|DD MMM YYYY,HH:mm]</code> — and <code>{'{Hi|Hello|Hey}'}</code> picks one at random each send.
                  </div>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', margin: '10px 0 4px' }}>Image (optional)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {editingRule.replyImageUrl && <img src={editingRule.replyImageUrl} alt="" style={{ height: 50, borderRadius: 6, border: '1px solid var(--border)' }} />}
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      {uploadingRuleImage ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : null}
                      {editingRule.replyImageUrl ? 'Replace Image' : 'Upload Image'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => pickRuleImage(e.target.files[0])} disabled={uploadingRuleImage} />
                    </label>
                    {editingRule.replyImageUrl && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingRule({ ...editingRule, replyImageUrl: '' })}>Remove</button>}
                  </div>
                </div>
              )}
              <div className="wa-stack" style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                    <Clock size={11} style={{ verticalAlign: -1, marginRight: 3 }} />Typing delay before reply (sec, 0 = off)
                  </label>
                  <input type="number" min={0} max={25} className="form-control" value={editingRule.presenceDelaySeconds}
                    onChange={e => setEditingRule({ ...editingRule, presenceDelaySeconds: Math.max(0, Math.min(25, parseInt(e.target.value, 10) || 0)) })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Indicator</label>
                  <select className="form-control" value={editingRule.presenceType} onChange={e => setEditingRule({ ...editingRule, presenceType: e.target.value })}>
                    <option value="typing">Typing…</option>
                    <option value="recording">Recording audio…</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  <ArrowRightCircle size={11} style={{ verticalAlign: -1, marginRight: 3 }} />Chain into another rule after replying (optional)
                </label>
                <select className="form-control" value={editingRule.nextRuleId || ''} onChange={e => setEditingRule({ ...editingRule, nextRuleId: e.target.value })}>
                  <option value="">— None —</option>
                  {rules.filter(r => r._id !== editingRule._id).map(r => <option key={r._id} value={r._id}>{(r.triggers || []).join(', ')}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingRule(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveRule} disabled={uploadingRuleImage}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const BTN_TYPE_LABEL = { url: 'Open URL', call: 'Call', quickReply: 'Quick Reply' };

// Editable reference library imported from the Deropo dashboard (2026-08-29) —
// separate from Message Center's own MessageTemplate set; edits here never touch
// Deropo itself. Usable as a chatbot keyword rule's reply (Automation tab). "Send
// Test" reuses the same send-buttons-test endpoint as the Overview tab's button-
// template card, so trying one of these out doesn't require re-typing it by hand.
function DeropoTemplatesTab() {
  const [templates, setTemplates] = useState(null);
  const [sendingId, setSendingId] = useState(null); // template _id currently showing the "to" prompt
  const [to, setTo] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // template being edited, or null

  const load = useCallback(() => {
    whatsappAPI.getDeropoTemplates().then(({ data }) => setTemplates(data.data || [])).catch(() => toast.error('Failed to load templates'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async (tpl) => {
    if (!to.trim()) return toast.error('Enter a number to send to');
    setBusyId(tpl._id);
    try {
      await whatsappAPI.sendButtonsTest({
        to: to.trim(), message: tpl.desc, footer: tpl.footer || undefined,
        buttons: tpl.buttons.map(b => ({ type: b.type, label: b.label || BTN_TYPE_LABEL[b.type], value: b.value })),
      });
      toast.success('Sent — check the phone.');
      setSendingId(null);
      setTo('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Remove "${tpl.name.replace(/\*/g, '')}" from this library? (Doesn't touch anything in Deropo itself.)`)) return;
    try {
      await whatsappAPI.deleteDeropoTemplate(tpl._id);
      load();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (!templates) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}><Loader size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: '.83rem', color: 'var(--text-muted)' }}>
          {templates.length} button templates (originally imported from Deropo, now just
          this local library), plus any added here directly. Editable, and not wired into
          Message Center. Usable as a chatbot
          keyword rule's reply (Automation tab). "Send Test" uses the button-message format
          (unverified — see Overview tab).
        </div>
        <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={() => setEditing('new')}>
          <Plus size={13} style={{ marginRight: 4 }} />Add Template
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {templates.map(tpl => (
          <div key={tpl._id} className="card">
            {tpl.imageUrl && (
              <img src={tpl.imageUrl} alt="" style={{ width: '100%', height: 140, objectFit: 'cover', borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }} />
            )}
            <div className="card-body">
              <div style={{ fontWeight: 700, fontSize: '.88rem', marginBottom: 6 }}>{tpl.name.replace(/\*/g, '')}</div>
              <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto', marginBottom: 8 }}>
                {tpl.desc}
              </div>
              {tpl.footer && <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>{tpl.footer}</div>}
              {tpl.buttons.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {tpl.buttons.map((b, i) => (
                    <span key={i} style={{ fontSize: '.72rem', padding: '3px 8px', borderRadius: 10, background: 'var(--bg-alt, #f3f4f6)', border: '1px solid var(--border)' }}>
                      {BTN_TYPE_LABEL[b.type] || b.type}{b.label ? `: ${b.label}` : ''}
                    </span>
                  ))}
                </div>
              )}
              {sendingId === tpl._id ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-control" style={{ fontSize: '.8rem' }} placeholder="919876543210" value={to} onChange={e => setTo(e.target.value)} autoFocus />
                  <button className="btn btn-primary btn-sm" onClick={() => sendTest(tpl)} disabled={busyId === tpl._id}>
                    {busyId === tpl._id ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={13} />}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setSendingId(null); setTo(''); }}><X size={13} /></button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setSendingId(tpl._id)}><Send size={13} style={{ marginRight: 4 }} />Send Test</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(tpl)}><Edit2 size={13} style={{ marginRight: 4 }} />Edit</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => remove(tpl)}><Trash2 size={13} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <DeropoTemplateEditModal template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

// template === null means "create a new one" — same form, POST instead of PUT.
function DeropoTemplateEditModal({ template, onClose, onSaved }) {
  const [name, setName] = useState(template?.name || '');
  const [desc, setDesc] = useState(template?.desc || '');
  const [footer, setFooter] = useState(template?.footer || '');
  const [imageUrl, setImageUrl] = useState(template?.imageUrl || '');
  const [uploading, setUploading] = useState(false);
  const [buttons, setButtons] = useState(template?.buttons?.length ? template.buttons : [{ type: 'url', label: '', value: '' }]);
  const [saving, setSaving] = useState(false);

  const addButton = () => buttons.length < 5 && setButtons([...buttons, { type: 'url', label: '', value: '' }]);
  const updateButton = (i, patch) => setButtons(buttons.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const removeButton = (i) => setButtons(buttons.filter((_, idx) => idx !== i));

  const pickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      setImageUrl(await uploadImageAbsolute(file));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!name.trim() || !desc.trim()) return toast.error('Name and message are required');
    setSaving(true);
    try {
      const payload = { name: name.trim(), desc, footer, imageUrl, buttons: buttons.filter(b => b.value.trim()) };
      if (template) {
        await whatsappAPI.updateDeropoTemplate(template._id, payload);
        toast.success('Template updated');
      } else {
        await whatsappAPI.createDeropoTemplate(payload);
        toast.success('Template created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{template ? 'Edit Template' : 'New Template'}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
            <input className="form-control" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Message</label>
            <textarea className="form-control" rows={5} value={desc} onChange={e => setDesc(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Footer</label>
            <input className="form-control" value={footer} onChange={e => setFooter(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Image</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {imageUrl && <img src={imageUrl} alt="" style={{ height: 60, borderRadius: 6, border: '1px solid var(--border)' }} />}
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                {uploading ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : null}
                {imageUrl ? 'Replace Image' : 'Upload Image'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => pickImage(e.target.files[0])} disabled={uploading} />
              </label>
              {imageUrl && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setImageUrl('')}>Remove</button>}
            </div>
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Buttons</label>
            {buttons.map((b, i) => (
              <div key={i} className="wa-btnrow" style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <select className="form-control" style={{ maxWidth: 120 }} value={b.type} onChange={e => updateButton(i, { type: e.target.value })}>
                  {BUTTON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input className="form-control" placeholder="Label" value={b.label} onChange={e => updateButton(i, { label: e.target.value })} />
                <input className="form-control" placeholder={b.type === 'call' ? 'Phone number' : b.type === 'url' ? 'https://…' : 'Reply id'} value={b.value} onChange={e => updateButton(i, { value: e.target.value })} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeButton(i)} disabled={buttons.length <= 1}><Trash2 size={13} /></button>
              </div>
            ))}
            {buttons.length < 5 && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={addButton}><Plus size={13} style={{ marginRight: 4 }} />Add Button</button>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || uploading}>
            {saving ? <Loader size={13} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} /> : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
